import { useEffect, useState } from "react";
import { fetchCurrentUserRateLimits } from "../api";
import { RateLimits } from "../types";
import { toast } from "react-toastify";
import { useAuth } from "../../auth/hooks";
import { getImpersonationDomain } from "../../impersonation/storage";

/**
 * Creates a skeleton RateLimits object with empty/null values
 */
function createSkeletonRateLimits(): RateLimits {
  return {
    id: "",
    created_at: "",
    domain: "",
    premium: null,
    align_rate_per_minute: null,
    align_lightning_rate_per_minute: null,
    align_token_rate_per_minute: null,
    align_lightning_token_rate_per_minute: null,
  };
}

export function useRateLimits() {
  const { isAuthenticated, isLoading: authLoading, userEmail } = useAuth();
  const [rateLimits, setRateLimits] = useState<RateLimits | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      return;
    }

    // The Limits data now comes from the standard customers CRUD endpoint, which
    // is keyed by domain. Resolve the effective domain the same way the rest of
    // the app does: the active "view as customer" target when a staff user is
    // impersonating, otherwise the signed-in user's own email domain. (A scope
    // change remounts the route tree, so this hook re-runs against the new
    // domain — see impersonation/storage.ts.)
    const domain =
      getImpersonationDomain() ?? userEmail?.split("@")[1]?.toLowerCase();

    if (!domain) {
      // Nothing to query (e.g. dev API-key mode, where there is no email). Leave
      // rateLimits null so the Limits section simply doesn't render.
      setIsLoading(false);
      return;
    }

    const loadRateLimits = async () => {
      try {
        const data = await fetchCurrentUserRateLimits(domain);
        setRateLimits(data);
        setErrorMessage(null);
      } catch (error: any) {
        // Log full error structure and message
        console.error("Failed to fetch rate limits - Error structure:", {
          error,
          errorMessage: error.message,
          errorResponse: error.response,
          errorData: error.response?.data,
          errorStatus: error.response?.status,
          errorStatusText: error.response?.statusText,
          fullErrorObject: JSON.stringify(error, Object.getOwnPropertyNames(error)),
        });
        console.error("Failed to fetch rate limits - Error message:", error.message);
        
        const errorMsg = error.message || "Failed to load rate limits";
        toast.error(errorMsg);
        
        // Set skeleton data with error message so UI can still render
        setRateLimits(createSkeletonRateLimits());
        setErrorMessage(errorMsg);
      } finally {
        setIsLoading(false);
      }
    };

    loadRateLimits();
  }, [isAuthenticated, authLoading, userEmail]);

  return { rateLimits, errorMessage, isLoading: isLoading || authLoading };
}

